//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import searchIcon from '../../../icons/searchIcon.svg';
import defaultAvatar from '../../../icons/DefaultAvatar.svg';
import warningIcon from '../../../icons/warningIcon.svg';
import { RawUser } from '../Landing';
import { useUserAuth } from '../../../UserAuth';
import { useTeamspaceContext } from '../../../TeamspaceContext';

/**
 * Maps a raw Keycloak role (e.g. `ROLE_ADMIN`) to a user-friendly display
 * label (`Admin`). Mirrors the behavior of `getDisplayRole` used by the User
 * Administration page so that role labels are consistent across the product.
 */
export const getDisplayRole = (role?: string | null): string => {
  if (!role) return '';
  if (role === 'ROLE_ADMIN' || role.toLowerCase() === 'admin') return 'Admin';
  if (role === 'ROLE_USER' || role.toLowerCase() === 'user') return 'User';
  if (role === 'ROLE_SUPPORT' || role.toLowerCase() === 'support')
    return 'Support';
  return role.replace(/^ROLE_/, '');
};

export interface InviteMemberProps {
  members: RawUser[];
  onMembersSelected: (selectedMembers: RawUser[]) => void;
  setNonMemberUsers: (nonMembers: RawUser[]) => void;
  mode?: 'loop' | 'teamspace';
}

/**
 * component for invite page for Loop/Teamspace
 * @param members it is the members list fetched from keycloak, which can be added to the teamspace
 * @param onMembersSelected function which sets what members are selected for adding them into teamspace
 * @param mode determines if this is for loop creation or teamspace creation
 * @returns
 */

interface NonMember {
  email: string;
}

const InviteMember: React.FC<InviteMemberProps> = ({
  members,
  onMembersSelected,
  setNonMemberUsers,
  mode = 'teamspace',
}) => {
  const { t } = useTranslation();
  const { user } = useUserAuth();
  const { teamspaceUsers } = useTeamspaceContext();

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsernames, setSelectedUsernames] = useState<Set<string>>(
    new Set(),
  );
  const [selectedMembersData, setSelectedMembersData] = useState<RawUser[]>([]);
  const [nonMembers, setNonMembers] = useState<NonMember[]>([]);
  const [warning, setWarning] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  // isAddDisabled is derived from state — see useMemo below
  const [selectedForAdd, setSelectedForAdd] = useState<RawUser | null>(null);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    onMembersSelected(selectedMembersData);
  }, [selectedMembersData, onMembersSelected]);

  const availableMembers = useMemo(
    () =>
      members
        .filter(
          (m) =>
            m.username !== user?.profile?.preferred_username &&
            m.email !== user?.profile?.email,
        )
        .sort((a, b) => a.fullname.trim().localeCompare(b.fullname.trim())),
    [members, user],
  );

  const filteredDropdownMembers = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.trim().toLowerCase();
    return availableMembers.filter(
      (m) =>
        m.fullname.trim().toLowerCase().includes(term) ||
        m.username.trim().toLowerCase().includes(term),
    );
  }, [searchTerm, availableMembers]);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const isTeamspaceUser = (input: string) => {
    if (!teamspaceUsers || teamspaceUsers.length === 0) return false;
    return teamspaceUsers.some(
      (u) => u.username.toLowerCase() === input.trim().toLowerCase(),
    );
  };

  const isCurrentUser = (input: string) => {
    const currentUsername = user?.profile?.preferred_username?.toLowerCase();
    const currentEmail = user?.profile?.email?.toLowerCase();
    const inputLower = input.trim().toLowerCase();
    return inputLower === currentUsername || inputLower === currentEmail;
  };

  const isAddDisabled = useMemo(() => {
    if (selectedForAdd) return false;
    if (!searchTerm.trim()) return true;

    const isEmailValid = isValidEmail(searchTerm.trim());
    const isValidMember = members.some(
      (m) =>
        m.fullname.toLowerCase() === searchTerm.trim().toLowerCase() ||
        m.username.toLowerCase() === searchTerm.trim().toLowerCase(),
    );

    return !(isValidMember || isEmailValid);
  }, [searchTerm, members, selectedForAdd]);

  const toggleMember = (member: RawUser) => {
    const newSet = new Set(selectedUsernames);
    if (newSet.has(member.username)) {
      newSet.delete(member.username);
      setSelectedMembersData((prev) =>
        prev.filter((m) => m.username !== member.username),
      );
    } else {
      newSet.add(member.username);
      setSelectedMembersData((prev) => [...prev, member]);
    }
    setSelectedUsernames(newSet);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setWarning('');
    setIsDropdownVisible(true);
    setSelectedForAdd(null);
  };

  const handleDropdownClick = (member: RawUser) => {
    setSelectedForAdd(member);
    setSearchTerm(member.username);
    setWarning('');
    setIsDropdownVisible(false);
  };

  const handleAddExternal = () => {
    const typed = searchTerm.trim();
    if (!typed) {
      setWarning(t('member-name-cannot-beempty'));
      return;
    }

    if (isCurrentUser(typed)) {
      setWarning(t('cannot-invite-yourself'));
      setSearchTerm('');
      setIsDropdownVisible(false);
      return;
    }

    if (selectedForAdd) {
      if (user?.profile?.preferred_username === selectedForAdd.username) {
        setWarning(t('cannot-invite-yourself'));
        setSelectedForAdd(null);
        setSearchTerm('');
        setIsDropdownVisible(false);
        return;
      }
      if (selectedUsernames.has(selectedForAdd.username)) {
        setWarning(t('exists-in-list'));
        setSelectedForAdd(null);
        setSearchTerm('');
        setIsDropdownVisible(false);
        return;
      }
      toggleMember(selectedForAdd);
      setStatusMessage(t('member-added-success'));
      setTimeout(() => setStatusMessage(''), 2000);
      setSelectedForAdd(null);
      setSearchTerm('');
      setIsDropdownVisible(false);
      return;
    }

    const existingMember = members.find(
      (m) =>
        m.username.toLowerCase() === typed.toLowerCase() ||
        m.fullname.toLowerCase() === typed.toLowerCase(),
    );

    if (existingMember) {
      if (user?.profile?.preferred_username === existingMember.username) {
        setWarning(t('cannot-invite-yourself'));
        setSearchTerm('');
        setIsDropdownVisible(false);
        return;
      }
      if (selectedUsernames.has(existingMember.username)) {
        setWarning(t('exists-in-list'));
        setSearchTerm('');
        setIsDropdownVisible(false);
        return;
      }
      toggleMember(existingMember);
      setStatusMessage(t('member-added-success'));
      setTimeout(() => setStatusMessage(''), 2000);
    } else if (isValidEmail(typed)) {
      if (mode === 'loop' && !isTeamspaceUser(typed)) {
        setWarning(t('users-available-teamspace'));
        setSearchTerm('');
        setIsDropdownVisible(false);
        return;
      }
      const emailAlreadyAdded = nonMembers.some((m) => m.email === typed);
      if (emailAlreadyAdded) {
        setWarning(t('exists-in-list'));
        setSearchTerm('');
        setIsDropdownVisible(false);
        return;
      }
      const updatedNonMembers: NonMember[] = [...nonMembers, { email: typed }];
      setNonMembers(updatedNonMembers);
      setNonMemberUsers(
        updatedNonMembers.map((m) => ({
          id: '',
          username: '',
          fullname: '',
          firstname: '',
          email: m.email,
          lastname: '',
          role: '',
        })),
      );
      setStatusMessage(t('member-added-success'));
      setTimeout(() => setStatusMessage(''), 2000);
    } else {
      setWarning(t('valid-member-name'));
    }

    setSearchTerm('');
    setIsDropdownVisible(false);
  };

  const handleRemoveNonMember = (email: string) => {
    setNonMembers((prev) => {
      const updated = prev.filter((m) => m.email !== email);
      setNonMemberUsers(
        updated.map((m) => ({
          id: '',
          username: '',
          fullname: '',
          firstname: '',
          email: m.email,
          lastname: '',
          role: '',
        })),
      );
      return updated;
    });
    setStatusMessage(t('member-removed-success'));
    setTimeout(() => setStatusMessage(''), 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      handleAddExternal();
    }
  };

  return (
    <div
      id="id-invite-member-container"
      data-testid="invite-member-container"
      className="relative flex flex-col p-4"
    >
      <h2 className="mb-3 text-base font-semibold text-gray-800 dark:text-gray-100">
        {t('add-members')}
      </h2>

      <button
        type="button"
        id="id-add-external-members-btn"
        data-testid="add-external-members-btn"
        onClick={() => setShowSearchBar((prev) => !prev)}
        className={`mb-3 w-full rounded border py-2.5 text-sm font-medium transition-colors ${
          showSearchBar
            ? 'border-brand-blue bg-brand-blue text-white'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
        }`}
      >
        {t('add-external-members')}
      </button>

      {showSearchBar && (
        <div
          id="id-invite-member-search-section"
          data-testid="invite-member-search-section"
          className="relative mb-3"
        >
          <div
            id="id-invite-member-search-row"
            data-testid="invite-member-search-row"
            className="flex items-center gap-2"
          >
            <div
              id="id-invite-member-search-input-wrapper"
              data-testid="invite-member-search-input-wrapper"
              className="relative flex-1"
            >
              <img
                src={searchIcon}
                alt="Search Icon"
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2"
              />
              <input
                type="text"
                aria-required="true"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={t('search-member')}
                className="w-full rounded border border-gray-300 py-2 pl-9 pr-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                onKeyDown={handleKeyDown}
              />
            </div>
            <button
              type="button"
              onClick={handleAddExternal}
              disabled={isAddDisabled}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('add-member')}
            </button>
          </div>

          {searchTerm &&
            filteredDropdownMembers.length > 0 &&
            isDropdownVisible && (
              <div
                id="id-invite-member-dropdown"
                data-testid="invite-member-dropdown"
                className="absolute z-10 mt-1 max-h-[200px] w-full overflow-y-auto rounded border border-gray-200 bg-white shadow-md dark:border-gray-600 dark:bg-gray-800"
              >
                {filteredDropdownMembers.map((member) => (
                  <button
                    key={member.username}
                    type="button"
                    id={`id-member-dropdown-item-${member.username}`}
                    data-testid={`member-dropdown-item-${member.username}`}
                    onClick={() => handleDropdownClick(member)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <img
                      src={defaultAvatar}
                      alt=""
                      className="size-8 rounded-full"
                    />
                    <div>
                      <div
                        id="id-invite-member-dropdown-fullname"
                        data-testid="invite-member-dropdown-fullname"
                        className="font-medium text-gray-800 dark:text-gray-100"
                      >
                        {member.fullname}
                      </div>
                      <div
                        id="id-invite-member-dropdown-username"
                        data-testid="invite-member-dropdown-username"
                        className="text-xs text-gray-500 dark:text-gray-400"
                      >
                        {member.username}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
        </div>
      )}

      {warning && (
        <div
          id="warningMessage"
          data-testid="invite-member-warning"
          className="mb-2 text-sm text-red-600 dark:text-red-400"
        >
          {warning}
        </div>
      )}
      {statusMessage && (
        <div
          id="statusMessage"
          data-testid="invite-member-status"
          className="mb-2 text-sm text-green-700 dark:text-green-400"
        >
          {statusMessage}
        </div>
      )}

      <hr className="border-gray-200 dark:border-gray-700" />

      <div
        id="id-invite-member-list"
        data-testid="invite-member-list"
        className="max-h-[350px] overflow-y-auto"
      >
        {nonMembers.map((nm) => (
          <div
            key={nm.email}
            id={`id-invite-member-non-member-${nm.email}`}
            data-testid={`invite-member-non-member-${nm.email}`}
            className="flex items-center gap-3 border-b border-gray-100 py-3 dark:border-gray-700"
          >
            <img
              src={defaultAvatar}
              alt="User"
              className="size-10 shrink-0 rounded-full"
            />
            <div
              id="id-invite-member-non-member-info"
              data-testid="invite-member-non-member-info"
              className="min-w-0 flex-1"
            >
              <div
                id="id-invite-member-non-member-email"
                data-testid="invite-member-non-member-email"
                className="text-sm text-gray-600 dark:text-gray-400"
              >
                {nm.email}
              </div>
              <div
                id="id-invite-member-non-member-warning"
                data-testid="invite-member-non-member-warning"
                className="mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400"
              >
                <img src={warningIcon} alt="" className="size-3.5" />
                <span
                  id="id-invite-member-non-member-warning-text"
                  data-testid="invite-member-non-member-warning-text"
                >
                  {t('not-teamspace-member')}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleRemoveNonMember(nm.email)}
              aria-label={`Remove ${nm.email} from invited members`}
              className="ml-2 text-gray-400 hover:text-red-500"
            >
              ✕
            </button>
          </div>
        ))}

        {availableMembers.map((member) => {
          const isSelected = selectedUsernames.has(member.username);
          return (
            <div
              key={member.username}
              id={`id-member-list-item-${member.username}`}
              data-testid={`member-list-item-${member.username}`}
              className="flex items-center gap-3 border-b border-gray-100 py-3 dark:border-gray-700"
            >
              <img
                src={defaultAvatar}
                alt="User"
                className="size-10 shrink-0 rounded-full"
              />
              <div
                id="id-invite-member-info"
                data-testid="invite-member-info"
                className="min-w-0 flex-1"
              >
                <div
                  id="id-invite-member-fullname"
                  data-testid="invite-member-fullname"
                  className="text-sm font-medium text-gray-800 dark:text-gray-100"
                >
                  {member.fullname}
                  {member.role && (
                    <span
                      id="id-invite-member-role"
                      data-testid="invite-member-role"
                      className="ml-1 font-normal text-gray-500 dark:text-gray-400"
                    >
                      ({getDisplayRole(member.role)})
                    </span>
                  )}
                </div>
                <div
                  id="id-invite-member-username"
                  data-testid="invite-member-username"
                  className="text-xs text-gray-500 dark:text-gray-400"
                >
                  {member.username}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isSelected}
                aria-label={
                  isSelected
                    ? `Remove ${member.fullname} from invited members`
                    : `Add ${member.fullname} to invited members`
                }
                onClick={() => toggleMember(member)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  isSelected ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  id="id-invite-member-toggle-thumb"
                  data-testid="invite-member-toggle-thumb"
                  className={`pointer-events-none inline-block size-4 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isSelected ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          );
        })}

        {availableMembers.length === 0 && nonMembers.length === 0 && (
          <div
            id="id-invite-member-empty-state"
            data-testid="invite-member-empty-state"
            className="py-8 text-center text-sm text-gray-500 dark:text-gray-400"
          >
            {t('search-and-add-member')}
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteMember;
